import { Component, computed, input, output, ViewEncapsulation } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { WrapperCabinetComponent } from './wrapper/wrapper.component';
import { HeaderCabinetComponent, HeaderLicenseStatus } from './header/header.component';
import { SidenavCabinetComponent } from './sidenav/sidenav.component';
import { ContentCabinetComponent } from './content/content.component';
import { WrapperContentCabinetComponent } from './wrapper-content/wrapper-content.component';
import { FooterCabinetComponent } from './footer/footer.component';
import { NavCabinetComponent } from './nav/nav.component';
import { NavChildCloseEvent, NavItem, NavSection } from './nav/nav.model';
import { NAV_ICONS } from './nav/nav-icons';
import { SkipLinkCabinetComponent } from './shell-a11y/skip-link.component';
import { RouteAnnouncerCabinetComponent } from './shell-a11y/route-announcer.component';
import { injectNarrowViewport } from './breakpoint';
import { attachEnhancedUi } from '../../enhanced-ui/attach-enhanced-ui';

/** Legacy rail entry: `icon` is a full SVG string or path data. */
export interface MenuItem {
  label: string;
  icon: string | readonly string[];
  route: string;
  badge?: number;
  badgeLabel?: string;
}

export interface MenuSection {
  heading?: string;
  items: MenuItem[];
}

const COLLAPSED_KEY = 'sidebarCollapsed';

function readCollapsed(): boolean {
  try {
    return JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? 'false') === true;
  } catch {
    return false;
  }
}

/** Path data from an SVG string (the `d` of every `<path>`). */
function pathsOf(icon: string | readonly string[]): readonly string[] {
  if (typeof icon !== 'string') return icon;
  if (!icon.includes('<')) return [icon];
  return Array.from(icon.matchAll(/\sd="([^"]+)"/g), (m) => m[1]);
}

/**
 * The cabinet shell used by the cloud and desktop apps: skip link, header,
 * rail (collapsible, a drawer on narrow screens), the routed page as the main
 * region, footer, and the page-change announcer. Apps supply the rail items,
 * a search field for the header centre (`[headerSearch]`),
 * a header status that comes and goes (`[headerStatus]`, first in the row),
 * header actions (`[headerActions]`), page banners (`[shellBanner]`) and an
 * overlay for the page area (`[contentOverlay]`).
 */
@Component({
  selector: 'app-shell-cabinet',
  standalone: true,
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    RouterOutlet,
    WrapperCabinetComponent,
    HeaderCabinetComponent,
    SidenavCabinetComponent,
    ContentCabinetComponent,
    WrapperContentCabinetComponent,
    FooterCabinetComponent,
    NavCabinetComponent,
    SkipLinkCabinetComponent,
    RouteAnnouncerCabinetComponent,
  ],
})
export class LayoutComponent {
  /** Rail groups in the legacy shape (desktop). */
  menuSections = input<MenuSection[]>([]);
  /** Rail groups; take precedence over `menuSections` when given. */
  navSections = input<readonly NavSection[] | null>(null);
  /** Rail items pinned to the bottom (e.g. CAD add-ins, Help, Send feedback). */
  pinnedItems = input<readonly NavItem[]>([
    { id: 'help', label: 'Help', icon: NAV_ICONS.help, route: '/user/help' },
  ]);
  shouldShowCompanyLogo = input<boolean>(false);
  currentCompany = input<{ name: string; logo_url?: string; owner_user?: { email: string } } | null>(null);
  licenseStatus = input<HeaderLicenseStatus | null>(null);
  pluginConnected = input<boolean>(false);
  aboutUrl = input('https://infinitform.com/about/');
  eulaUrl = input('https://infinitform.com/eula/');
  privacyUrl = input('https://infinitform.com/privacy-policy/');

  isSidebarCollapsed = readCollapsed();
  isSidebarOpen = false;

  toggleSidebar = output<void>();
  badgeClick = output<void>();
  /** A rail action item was activated (e.g. 'feedback'). */
  navAction = output<string>();
  /** A child's close button in the rail was activated. */
  readonly navChildClose = output<NavChildCloseEvent>();

  readonly sections = computed<readonly NavSection[]>(
    () =>
      this.navSections() ??
      this.menuSections().map((section, index) => ({
        id: `section-${index}`,
        heading: section.heading,
        items: section.items.map((item) => ({
          id: item.route,
          label: item.label,
          icon: pathsOf(item.icon),
          route: item.route,
          badge: item.badge,
          badgeLabel: item.badgeLabel,
        })),
      })),
  );

  readonly pinnedSections = computed<readonly NavSection[]>(() =>
    this.pinnedItems().length ? [{ id: 'pinned', items: this.pinnedItems() }] : [],
  );

  /** The rail label of the page at `path`, for routes without a title. */
  readonly fallbackTitle = (path: string): string | null => {
    const items = [...this.sections(), ...this.pinnedSections()].flatMap((s) => s.items);
    const match = items
      .filter((item) => item.route && (path === item.route || path.startsWith(`${item.route}/`)))
      .sort((a, b) => (b.route?.length ?? 0) - (a.route?.length ?? 0))[0];
    return match?.label ?? null;
  };

  private readonly narrow = injectNarrowViewport();

  constructor() {
    // Enhanced UI effects show in the cabinet, with the value from the user's settings.
    attachEnhancedUi({ syncFromUserSettings: true });
  }

  onToggleSidebar(): void {
    if (this.narrow()) {
      this.isSidebarOpen = !this.isSidebarOpen;
    } else {
      this.isSidebarCollapsed = !this.isSidebarCollapsed;
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify(this.isSidebarCollapsed));
      } catch {
        /* storage unavailable: the choice lasts for this session */
      }
    }
    this.toggleSidebar.emit();
  }

  closeSidebar(): void {
    this.isSidebarOpen = false;
  }
}
